import { Row } from "@tanstack/react-table";

import AvatarText from "@/components/reusables/AvatarText";
import ProgressLabel from "@/components/reusables/ProgressLabel";
import { CustomDropdown } from "@/components/reusables/custom-action-button";
import { Checkbox } from "@/components/ui/checkbox";
import { SELECTOR_ID } from "@/constants/global-constants";
import { ExtendedColumnDef } from "@/interfaces/globals";
import { IProject } from "@/interfaces/project.interface";
import { cn, removeHyphens } from "@/lib/utils";
import { dateFormat } from "@/utils/helper-functions/date-time.helper";

type ColumnDependencies = {
  writePermission: boolean;
  getActionOptions: (row: Row<IProject>) => ActionButtonDataTypes[];
};

export const useProjectColumns = ({
  writePermission,
  getActionOptions,
}: ColumnDependencies): ExtendedColumnDef<IProject>[] => {
  return [
    {
      map: {
        key: "id",
        valueFrom: "id",
      },
      id: SELECTOR_ID,
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label='Select all'
          className={cn(
            "hidden group-hover/header:block",
            table.getIsSomePageRowsSelected() && "block",
            table.getIsAllPageRowsSelected() && "!block",
          )}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label='Select row'
        />
      ),

      enableSorting: false,
      enableHiding: false,
    },
    {
      sortBy: "name",
      map: {
        key: "Project Name",
        dataTransformer: (data): string => data?.name || "-",
      },
      meta: {
        showRightBorder: true,
        minWidth: 300,
      },
      accessorKey: "Project Name",
      accessorFn: (data) => data?.name.toLowerCase() || "-",
      header: "Project name",
      cell: ({ row }) => (
        <div className='flex items-center' title={row.original.name}>
          <div className='font-normal text-gray-700 dark:text-white min-w-fit line-clamp-2'>
            {row.original.name}
          </div>
        </div>
      ),
    },
    {
      map: {
        key: "Address",
        dataTransformer: (data): string => data?.address?.label || "-",
      },
      meta: {
        showRightBorder: true,
        minWidth: 300,
      },
      accessorKey: "Address",
      accessorFn: (data) => data?.address?.label.toLowerCase() || "-",
      header: "Address",
      cell: ({ row }) => (
        <div className='flex items-center' title={row.original.address?.label}>
          <div className='font-normal text-gray-700 dark:text-white min-w-fit line-clamp-2'>
            {row.original.address?.label}
          </div>
        </div>
      ),
      enableSorting: false,
    },
    {
      sortBy: "ourReferenceNumber",
      map: {
        key: "Our Reference Number",
        dataTransformer: (data): string =>
          removeHyphens(data?.ourReferenceNumber?.toUpperCase()) || "-",
      },
      meta: {
        showRightBorder: true,
      },
      accessorKey: "Our Reference Number",
      accessorFn: (data) =>
        removeHyphens(data?.ourReferenceNumber?.toUpperCase()) || "-",
      header: "Our Reference Number",
      cell: ({ row }) => (
        <div className='text-gray-700 dark:text-white text-clip uppercase'>
          {removeHyphens(row.original?.ourReferenceNumber) || "-"}
        </div>
      ),
    },
    {
      sortBy: "customerName",
      map: {
        key: "Customer",
        dataTransformer: (data): string => data?.customer?.name || "-",
      },
      meta: {
        showRightBorder: true,
        minWidth: 250,
      },
      accessorKey: "Customer",
      accessorFn: (data) => data?.customer?.name?.toLowerCase(),
      header: "Customer",
      cell: ({ row }) => (
        <AvatarText
          name={row.original.customer?.name || ""}
          src={row.original?.customer?.logo}
          title={row.original.customer?.name}
        />
      ),
    },
    {
      sortBy: "customerProjectReference",
      map: {
        key: "Customer Ref No.",
        dataTransformer: (data): string =>
          data?.customerProjectReference || "-",
      },
      meta: {
        showRightBorder: true,
        minWidth: 300,
      },
      accessorKey: "customer Reference Number",
      accessorFn: (data) => data?.customerProjectReference || "-",
      header: "Customer Ref No.",
      cell: ({ row }): React.JSX.Element => {
        return (
          <div className='text-left text-gray-700 dark:text-white line-clamp-2'>
            {row.original?.customerProjectReference || "-"}
          </div>
        );
      },
    },
    {
      map: {
        key: "Budget Hrs",
        dataTransformer: ({ budgetHours }): string => {
          return (budgetHours ?? 0)?.toFixed(2)?.toString();
        },
      },
      meta: {
        showRightBorder: true,
        className: "min-w-[250px] ",
      },
      accessorKey: "Budget Hrs",
      header: "Budget Hrs",
      cell: ({
        row: {
          original: { budgetHours, usedWorkHours },
        },
      }): React.JSX.Element => {
        budgetHours = budgetHours ?? 0;
        const budgetHour =
          budgetHours > 0 ? Number((usedWorkHours / budgetHours) * 100) : 0;

        return (
          <ProgressLabel
            label={`${budgetHour?.toFixed(2)} hrs`}
            value={budgetHour}
            color='#00BAD1'
            tooltip={{
              toolTipContent: (
                <p>
                  Budget Hrs:{budgetHour?.toFixed(2)} <br /> Consumed Hrs:
                  {usedWorkHours?.toFixed(2)}
                </p>
              ),
            }}
          />
        );
      },
      enableSorting: false,
    },
    {
      map: {
        key: "RECYCLING",
        dataTransformer: (data): string => {
          const isReportSummary = data?.reportSummary;

          const recyclePercentage =
            isReportSummary?.recyclingPercentage?.recyclingPercentage?.toFixed(
              2,
            ) || 0;

          return recyclePercentage.toString();
        },
      },
      meta: {
        showRightBorder: true,
        className: "min-w-[250px]",
      },
      accessorKey: "RECYCLING",
      header: "Recycling",
      cell: ({ row }): React.JSX.Element => {
        const isReportSummary = row?.original?.reportSummary;

        const recyclePercentage =
          isReportSummary?.recyclingPercentage?.recyclingPercentage?.toFixed(
            2,
          ) || 0;

        const isGreenStartProject = row?.original?.isGreenStarProject;

        const getColor = (): string => {
          if (isGreenStartProject) {
            return Number(recyclePercentage) < 90 ? "#FCBE62" : "#23AD5B";
          }

          return Number(recyclePercentage) < 80 ? "#FCBE62" : "#23AD5B";
        };

        return (
          <ProgressLabel
            label={`Recycle: ${recyclePercentage}%`}
            value={Number(recyclePercentage) || 0}
            color={getColor()}
          />
        );
      },
      enableSorting: false,
    },
    {
      map: {
        key: "Project Progress",
        dataTransformer: ({ projectProgress }): string => {
          return (projectProgress ?? 0).toFixed(2).toString();
        },
      },
      meta: {
        showRightBorder: true,
        className: "min-w-[250px]",
      },
      accessorKey: "Project Progress",
      header: "Project Progress",
      cell: ({
        row: {
          original: { projectProgress },
        },
      }): React.JSX.Element => {
        return (
          <ProgressLabel
            label={`${(projectProgress ?? 0).toFixed(2)} %`}
            value={Number(projectProgress) || 0}
            color='#FF9F43'
          />
        );
      },
      enableSorting: false,
    },
    {
      sortBy: "lastActivityDate",
      map: {
        key: "Last Activity Date",
        dataTransformer: (data): string =>
          dateFormat(data?.lastActivityDate) || "-",
      },
      accessorKey: "LAST ACTIVITY DATE",
      accessorFn: (data) => data.lastActivityDate,
      header: "Last Activity Date",
      cell: ({ row }) => (
        <div className='text-gray-700 capitalize dark:text-white'>
          {dateFormat(row.original.lastActivityDate)}
        </div>
      ),
      meta: {
        showRightBorder: true,
        minWidth: 200,
      },
    },
    {
      sortBy: "startDate",
      map: {
        key: "Start Date",
        dataTransformer: (data): string => dateFormat(data?.startDate) || "-",
      },
      accessorKey: "START DATE",
      accessorFn: (data) => data.startDate,
      header: "Start Date",
      cell: ({ row }) => (
        <div className='text-gray-700 capitalize dark:text-white'>
          {dateFormat(row.original.startDate)}
        </div>
      ),
      meta: {
        showRightBorder: true,
        minWidth: 200,
      },
    },
    {
      sortBy: "endDate",
      map: {
        key: "End Date",
        dataTransformer: (data): string =>
          data.endDate ? dateFormat(data?.endDate) : "-",
      },
      accessorKey: "end Date",
      accessorFn: (data) => data.endDate,
      header: "End Date",
      cell: ({
        row: {
          original: { endDate },
        },
      }) => (
        <div className='text-gray-700 capitalize dark:text-white'>
          {endDate ? dateFormat(endDate) : "-"}
        </div>
      ),
    },
    {
      map: {
        key: "",
        excludeFromExport: true,
      },
      id: "actions",
      hideColumn: !writePermission,
      cell: ({ row }): React.JSX.Element => {
        return (
          <>
            <CustomDropdown data={getActionOptions(row)} />
          </>
        );
      },
    },
  ];
};
